import { useState } from 'react';
import { DEFAULT_PAGE_SIZE } from '@forecastify/shared';
import type { PagerState } from '@forecastify/ui';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The pager over a list the screen already holds in full.                    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * CLAUDE.md §3's settled pager — `[<] [selector] [>] [total]` — on the screens whose endpoints
 * still answer with every row: contracts, invoices and both kinds of request. Their controllers
 * record why they are not paginated yet (*"the first customer whose list does not fit one screen
 * needs the cursor"*); until then the screen filters and sorts in memory, and this pages the
 * result. `use-pages.ts` is the server-side twin, and the control is the same either way.
 *
 * ⚠️⚠️ **PAGES THE FILTERED, SORTED ROWS — never the raw list.** Paging first and filtering the
 * page is the bug item 35 removed from the project list: the search box searched fifty rows.
 *
 * ⚠️ **`resetKey` is what the SET depends on** — search, filters, view and sort, joined. A change
 * goes back to page one, `use-pages.ts`'s rule. It is a key rather than the rows' identity,
 * because a live refresh hands over a new array of the same set, and that must not throw somebody
 * off page three.
 *
 * ⚠️ **The index is clamped on every render**, so a set that shrinks under the reader — a decided
 * request leaving the pending view — lands on its last page rather than on an empty one.
 */
export function useRowPages<T>(
  rows: readonly T[],
  resetKey: string,
  limit: number = DEFAULT_PAGE_SIZE,
): { readonly rows: T[]; readonly page: PagerState } {
  const [wanted, setWanted] = useState(0);
  const [key, setKey] = useState(resetKey);
  /* ⚠️ React's own pattern for state that resets with a prop: set during render, no effect and
     no frame showing the old page of the new set. */
  if (key !== resetKey) {
    setKey(resetKey);
    setWanted(0);
  }

  const count = Math.max(1, Math.ceil(rows.length / limit));
  const index = Math.min(wanted, count - 1);
  const goTo = (to: number): void => {
    setWanted(Math.min(Math.max(0, to), count - 1));
  };

  return {
    rows: rows.slice(index * limit, index * limit + limit),
    page: {
      hasPrevious: index > 0,
      hasNext: index + 1 < count,
      onPrevious: () => {
        goTo(index - 1);
      },
      onNext: () => {
        goTo(index + 1);
      },
      index,
      count,
      total: rows.length,
      onGoTo: goTo,
      /* 3 Nov 2026 · a short last page keeps a full page's height (`pageFiller`). */
      pageSize: limit,
    },
  };
}
